// src/components/ui/Breadcrumb.jsx - Componente de Navegação Breadcrumb (COMPLETO E CORRIGIDO)
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// ==================== ÍCONES SVG ====================

const ChevronRightIcon = ({ className = 'w-4 h-4' }) => (
  <svg 
    className={className} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const HomeIcon = ({ className = 'w-4 h-4' }) => (
  <svg 
    className={className} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

// ==================== COMPONENTE ITEM BREADCRUMB ====================

const BreadcrumbItem = ({ item, isLast, isFirst }) => {
  const { label, path } = item;

  return (
    <li className="inline-flex items-center">
      {!isFirst && (
        <ChevronRightIcon className="flex-shrink-0 w-4 h-4 text-gray-400 mx-1" />
      )}

      <div className="flex items-center">
        {isFirst && (
          <HomeIcon className="flex-shrink-0 w-4 h-4 text-gray-500 mr-2" />
        )}

        {isLast || !path ? (
          <span className="text-sm font-medium text-gray-500 truncate max-w-40">
            {label}
          </span>
        ) : (
          <Link
            to={path}
            className="text-sm font-medium text-gray-700 hover:text-primary-600 truncate max-w-40 transition-colors"
          >
            {label}
          </Link>
        )}
      </div>
    </li>
  );
};

// ==================== COMPONENTE BREADCRUMB BÁSICO ====================

export const Breadcrumb = ({ items = [], className = '' }) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <nav className={`flex ${className}`} aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-3">
        {items.map((item, index) => (
          <BreadcrumbItem
            key={`${item.path || 'item'}-${index}`}
            item={item}
            isLast={index === items.length - 1}
            isFirst={index === 0}
          />
        ))}
      </ol>
    </nav>
  );
};

// ==================== BREADCRUMB MOBILE ====================

export const BreadcrumbMobile = ({ items = [], onBack }) => {
  if (!items || items.length === 0) return null;

  const currentItem = items[items.length - 1];
  const previousItem = items[items.length - 2];

  return (
    <nav className="flex items-center space-x-2 lg:hidden" aria-label="Breadcrumb mobile">
      {previousItem && (
        <button
          onClick={onBack || (() => window.history.back())}
          className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          aria-label="Voltar"
        >
          <ChevronRightIcon className="w-5 h-5 rotate-180" />
        </button>
      )}
      
      <div className="flex items-center min-w-0">
        <span className="text-sm font-medium text-gray-900 truncate">
          {currentItem.label}
        </span>
      </div>
    </nav>
  );
};

// ==================== BREADCRUMB COM ESTILOS ====================

export const BreadcrumbStyled = ({ 
  items = [], 
  variant = 'default', 
  size = 'md',
  showHomeIcon = true,
  className = '' 
}) => {
  if (!items || items.length === 0) return null;

  const variants = {
    default: 'text-gray-700',
    primary: 'text-primary-700', 
    secondary: 'text-gray-600',
    dark: 'text-gray-900'
  };

  const sizes = {
    sm: { text: 'text-xs', icon: 'w-3 h-3' },
    md: { text: 'text-sm', icon: 'w-4 h-4' }, 
    lg: { text: 'text-base', icon: 'w-5 h-5' }
  };

  const sizeConfig = sizes[size];
  const variantClass = variants[variant] || variants.default;

  return (
    <nav className={`flex ${className}`} aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-3">
        {items.map((item, index) => (
          <li key={`${item.path || 'item'}-${index}`} className="inline-flex items-center">
            {index > 0 && (
              <ChevronRightIcon className={`flex-shrink-0 ${sizeConfig.icon} text-gray-400 mx-1`} />
            )}

            <div className="flex items-center">
              {index === 0 && showHomeIcon && (
                <HomeIcon className={`flex-shrink-0 ${sizeConfig.icon} text-gray-500 mr-2`} />
              )}

              {index === items.length - 1 || !item.path ? (
                <span className={`${sizeConfig.text} font-medium text-gray-500 truncate max-w-40`}>
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.path}
                  className={`${sizeConfig.text} font-medium ${variantClass} hover:text-primary-600 truncate max-w-40 transition-colors`}
                >
                  {item.label}
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
};

// ==================== BREADCRUMB RESPONSIVO ====================

export const ResponsiveBreadcrumb = ({ items, userProfile }) => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    navigate(-1);
  };
  
  return (
    <>
      <div className="hidden lg:block">
        <BreadcrumbStyled items={items} />
      </div>
      
      <div className="lg:hidden">
        <BreadcrumbMobile items={items} onBack={handleBack} />
      </div>
    </>
  );
};

// ==================== BREADCRUMB COMPACTO ====================

export const BreadcrumbCompact = ({ items = [], maxItems = 2 }) => {
  if (!items || items.length === 0) return null;

  const displayItems = items.length > maxItems ? 
    [items[0], ...items.slice(-maxItems + 1)] : 
    items;

  const hiddenCount = items.length - displayItems.length;

  return (
    <nav className="flex items-center space-x-1 text-xs" aria-label="Breadcrumb compacto">
      {hiddenCount > 0 && (
        <span className="text-gray-400 px-1">+{hiddenCount}</span>
      )}
      
      {displayItems.map((item, index) => (
        <div key={`compact-${item.path || 'item'}-${index}`} className="flex items-center">
          {index > 0 && (
            <ChevronRightIcon className="w-3 h-3 text-gray-400 mx-1" />
          )}
          
          {index === displayItems.length - 1 || !item.path ? (
            <span className="text-gray-600 font-medium truncate max-w-24">
              {item.label}
            </span>
          ) : (
            <Link
              to={item.path}
              className="text-primary-600 hover:text-primary-800 font-medium truncate max-w-24 transition-colors"
            >
              {item.label}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
};

// ==================== DROPDOWN PARA ITENS OCULTOS ====================

const BreadcrumbDropdown = ({ hiddenItems }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!hiddenItems || hiddenItems.length === 0) return null;

  return (
    <li className="inline-flex items-center relative">
      <ChevronRightIcon className="flex-shrink-0 w-4 h-4 text-gray-400 mx-1" />
      
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors px-2 py-1 rounded-md hover:bg-gray-100"
          aria-label="Mostrar itens ocultos"
        >
          <span>...</span>
          <ChevronRightIcon className="w-3 h-3 transform rotate-90" />
        </button>

        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsOpen(false)}
            />
            
            <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20">
              <div className="py-1">
                {hiddenItems.map((item, index) => (
                  <Link
                    key={`dropdown-${item.path || 'item'}-${index}`}
                    to={item.path}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </li>
  );
};

// ==================== BREADCRUMB COM DROPDOWN ====================

export const BreadcrumbWithDropdown = ({ items = [], className = '' }) => {
  if (!items || items.length === 0) return null;

  const shouldShowDropdown = items.length > 4;
  const visibleItems = shouldShowDropdown ? 
    [items[0], '...', ...items.slice(-2)] : 
    items;

  return (
    <nav className={`flex ${className}`} aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-3">
        {visibleItems.map((item, index) => {
          if (item === '...') {
            return (
              <BreadcrumbDropdown 
                key="dropdown"
                hiddenItems={items.slice(1, -2)}
              />
            );
          }
          
          return (
            <BreadcrumbItem
              key={`${item.path || 'item'}-${index}`}
              item={item}
              isLast={index === visibleItems.length - 1}
              isFirst={index === 0}
            />
          );
        })}
      </ol>
    </nav>
  );
};

// ==================== BREADCRUMB COM AÇÕES ====================

export const BreadcrumbWithActions = ({ 
  items = [], 
  actions = [], 
  className = '' 
}) => {
  if (!items || items.length === 0) return null;

  return (
    <div className={`flex items-center justify-between ${className}`}>
      <Breadcrumb items={items} />
      
      {actions.length > 0 && (
        <div className="flex items-center space-x-2">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`
                px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                ${action.variant === 'primary' 
                  ? 'bg-primary-600 text-white hover:bg-primary-700' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }
              `}
              disabled={action.disabled}
            >
              {action.icon && <span className="mr-1">{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== HOOKS ====================

export const useBreadcrumb = () => {
  const location = useLocation();
  
  const generateAutoBreadcrumbs = (userProfile) => {
    return generateBreadcrumbsFromPath(location.pathname, userProfile);
  };

  return {
    currentPath: location.pathname,
    generateAutoBreadcrumbs
  };
};

export const useContextualBreadcrumb = (userProfile) => {
  const location = useLocation();
  
  const getContextualBreadcrumbs = () => {
    const baseBreadcrumbs = generateBreadcrumbsFromPath(location.pathname, userProfile);
    
    if (userProfile?.userType === 'instituicao') {
      baseBreadcrumbs[0] = { 
        label: 'Painel Institucional', 
        path: '/dashboard' 
      };
    } else if (userProfile?.userType === 'supervisor') {
      baseBreadcrumbs[0] = { 
        label: 'Painel Supervisor', 
        path: '/dashboard' 
      };
    } else if (userProfile?.userType === 'internacional') {
      baseBreadcrumbs[0] = { 
        label: 'Painel Global', 
        path: '/dashboard' 
      };
    }
    
    return baseBreadcrumbs;
  };

  return {
    breadcrumbs: getContextualBreadcrumbs(),
    currentPath: location.pathname
  };
};

// ==================== UTILITÁRIOS ====================

export const generateBreadcrumbsFromPath = (path, userProfile) => {
  const breadcrumbs = [
    { label: 'Dashboard', path: '/dashboard' }
  ];

  if (path.includes('/agenda-digital/')) {
    breadcrumbs.push({ 
      label: 'Agenda Digital', 
      path: '/dashboard/agenda-digital/gerenciar' 
    });
    
    if (path.includes('/criar')) {
      breadcrumbs.push({ label: 'Criar Agenda' });
    } else if (path.includes('/gerenciar')) {
      breadcrumbs.push({ label: 'Gerenciar Consultas' });
    } else if (path.includes('/configurar')) {
      breadcrumbs.push({ label: 'Configurações' });
    } else if (path.includes('/disponibilidade')) {
      breadcrumbs.push({ label: 'Disponibilidade' });
    } else if (path.includes('/link-personalizado')) {
      breadcrumbs.push({ label: 'Link Personalizado' });
    }
  }
  
  else if (path.includes('/supervisao/')) {
    breadcrumbs.push({ 
      label: 'Supervisão', 
      path: '/dashboard/supervisao/minhas' 
    });
    
    if (path.includes('/buscar')) {
      breadcrumbs.push({ label: 'Buscar Supervisor' });
    } else if (path.includes('/agendar')) {
      breadcrumbs.push({ label: 'Agendar Sessão' });
    } else if (path.includes('/minhas')) {
      breadcrumbs.push({ label: 'Minhas Supervisões' });
    } else if (path.includes('/matching')) {
      breadcrumbs.push({ label: 'Sugestões IA' });
    } else if (path.includes('/historico')) {
      breadcrumbs.push({ label: 'Histórico' });
    }
  }
  
  else if (path.includes('/supervisor/')) {
    breadcrumbs.push({ 
      label: 'Supervisionados', 
      path: '/dashboard/supervisor/supervisionados' 
    });
    
    if (path.includes('/agenda')) {
      breadcrumbs.push({ label: 'Agenda de Supervisão' });
    } else if (path.includes('/relatorios')) {
      breadcrumbs.push({ label: 'Relatórios' });
    } else if (path.includes('/supervisionados')) {
      breadcrumbs.push({ label: 'Lista de Supervisionados' });
    } else if (path.includes('/configuracoes')) {
      breadcrumbs.push({ label: 'Configurações de Supervisão' });
    }
  }
  
  else if (path.includes('/instituicao/')) {
    breadcrumbs.push({ 
      label: 'Gestão Institucional', 
      path: '/dashboard/instituicao/profissionais' 
    });
    
    if (path.includes('/profissionais')) {
      breadcrumbs.push({ label: 'Profissionais' });
    } else if (path.includes('/relatorios')) {
      breadcrumbs.push({ label: 'Relatórios Institucionais' });
    } else if (path.includes('/configuracoes')) {
      breadcrumbs.push({ label: 'Configurações Institucionais' });
    } else if (path.includes('/departamentos')) {
      breadcrumbs.push({ label: 'Departamentos' });
    }
  }
  
  else if (path.includes('/perfil')) {
    breadcrumbs.push({ label: 'Perfil' });
    
    if (path.includes('/editar')) {
      breadcrumbs.push({ label: 'Editar Perfil' });
    } else if (path.includes('/documentos')) {
      breadcrumbs.push({ label: 'Documentos' });
    }
  } 
  
  else if (path.includes('/configuracoes')) {
    breadcrumbs.push({ label: 'Configurações' });
    
    if (path.includes('/conta')) {
      breadcrumbs.push({ label: 'Conta' });
    } else if (path.includes('/notificacoes')) {
      breadcrumbs.push({ label: 'Notificações' });
    } else if (path.includes('/privacidade')) {
      breadcrumbs.push({ label: 'Privacidade' });
    } else if (path.includes('/assinatura')) {
      breadcrumbs.push({ label: 'Assinatura' });
    }
  } 
  
  else if (path.includes('/suporte')) {
    breadcrumbs.push({ label: 'Suporte' });
    
    if (path.includes('/tickets')) {
      breadcrumbs.push({ label: 'Meus Tickets' });
    } else if (path.includes('/faq')) {
      breadcrumbs.push({ label: 'FAQ' });
    } else if (path.includes('/contato')) {
      breadcrumbs.push({ label: 'Contato' });
    }
  }

  else if (path.includes('/relatorios')) {
    breadcrumbs.push({ label: 'Relatórios' });
    
    if (path.includes('/financeiro')) {
      breadcrumbs.push({ label: 'Financeiro' });
    } else if (path.includes('/atividade')) {
      breadcrumbs.push({ label: 'Atividade' });
    } else if (path.includes('/desempenho')) {
      breadcrumbs.push({ label: 'Desempenho' });
    }
  }

  return breadcrumbs;
};

// ==================== EXPORTS ====================

export default Breadcrumb;

export {
  ChevronRightIcon,
  HomeIcon
};